/**
 * Conversations provider: state, messaging, and group management with WebSocket updates.
 *
 * PRIVACY: Messages are E2E encrypted before leaving the device. The server only sees
 * ciphertext; decryption happens exclusively client-side.
 *
 * @module hooks/conversations/ConversationsProvider
 */

import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import {
  createApiClient,
  type PublicConversation,
  type PublicGroupInvite,
  type PublicIdentity,
  type ClaimedDevicePreKeys,
} from '@adieuu/shared';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { useInbox } from '../useInbox';
import { useIdentity } from '../useIdentity';
import { useChatSocket } from '../useChatSocket';
import { useAppConfig, usePlatformCapabilities } from '../../config';
import { useToast } from '../../components/Toast';
import {
  useNotificationSoundPreference,
  useTtlNotificationSoundPreference,
  useMentionNotificationSoundPreference,
} from '../useNotificationSoundPreference';
import { useClaimAchievement } from '../useClaimAchievement';
import { useNotificationDisplaySettings } from '../useNotificationDisplaySettings';
import { fireConversationNotification } from '../../utils/conversationNotifications';
import {
  unwrapConversationMetadataKey,
  type RecipientKeys,
  type MemberSettingsMap,
} from '../../services/conversationCryptoService';
import {
  conversationHasUnreadFromReadState,
  createReadPointerPersistQueue,
  decryptConversationMemberSettingsForDisplay,
  decryptConversationNameForDisplay,
  decryptConversationReadStateForDisplay,
  decryptSystemEventForDisplay,
  ensureConversationMetadataKey,
  persistConversationReadPointer,
  preferredReadPointer,
  readPointerToPersistOnDeactivate,
} from '../../services/conversationMetadataService';
import { loadDecryptKeysQuiet } from './conversationDecryptKeys';
import { evaluatePeerTrust, throwIfPeerTrustBlocked } from '../../services/peerTrust';
import { isPeerTrustPauseEnabled } from '../usePeerTrustPausePreference';
import { getPersistedSessionKey } from '../../services/preKeyStorage';
import { getSessionKeysForMessages as loadSessionKeysForMessages } from '../../services/sessionKeyRetrieval';
import {
  EMPTY_MEMBER_SETTINGS,
  EMPTY_MESSAGES,
  type ConversationMessagesState,
  type DecryptedConversation,
  type DisplayMessage,
  type ConversationsContextValue,
} from './types';
import { ConversationsContext } from './context';
import { useConversationParticipantProfiles } from './useConversationParticipantProfiles';
import { useSignatureRefresh } from './useSignatureRefresh';
import { useConversationDataFetching } from './useConversationDataFetching';
import { useConversationsSocketEffects } from './useConversationsSocketEffects';
import { useConversationsAuthLifecycleEffects } from './useConversationsAuthLifecycleEffects';
import { useConversationCreateAndSend } from './useConversationCreateAndSend';
import { useConversationGroupInvitesAndDelete } from './useConversationGroupInvitesAndDelete';
import { useConversationPreferences } from '../useConversationPreferences';

// ============================================================================
// Provider
// ============================================================================

interface ConversationsProviderProps {
  children: ReactNode;
}

export function ConversationsProvider({ children }: ConversationsProviderProps) {
  const { session } = useAuth();
  const { status: identityStatus, identity, getSigningKey, getPqSigningKey, getCurrentDeviceId, getWrappingKey } =
    useIdentity();
  const { apiBaseUrl } = useAppConfig();
  const { subscribe, onStateChange } = useChatSocket();
  const { t } = useTranslation();
  const toast = useToast();
  const { notifications, audio } = usePlatformCapabilities();
  const soundPref = useNotificationSoundPreference();
  const ttlSoundPref = useTtlNotificationSoundPreference();
  const mentionSoundPref = useMentionNotificationSoundPreference();
  const claimAchievement = useClaimAchievement();
  const navigate = useNavigate();
  const { display: notificationDisplay } = useNotificationDisplaySettings();

  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const { markItemsReadForConversation, setAutoReadConversation, refreshUnseen } = useInbox();
  const {
    preferences: conversationPreferences,
    loading: prefsLoading,
    mergeEncryptedReadState,
  } = useConversationPreferences();
  const isLoggedIn = identityStatus === 'logged_in' && !!identity;

  const identityRef = useRef(identity);
  identityRef.current = identity;

  // State
  const [conversations, setConversations] = useState<DecryptedConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messagesState, setMessagesState] = useState<Record<string, ConversationMessagesState>>({});
  /** Reply-quote parents fetched outside the main buffer (per conversation). */
  const [replyParentHydration, setReplyParentHydration] = useState<
    Record<string, Record<string, DisplayMessage>>
  >({});
  const replyParentHydrationRef = useRef(replyParentHydration);
  useEffect(() => {
    replyParentHydrationRef.current = replyParentHydration;
  }, [replyParentHydration]);
  const replyHydrationInflightRef = useRef(new Set<string>());
  const [invites, setInvites] = useState<PublicGroupInvite[]>([]);
  const [pendingInvitesRefreshSignal, setPendingInvitesRefreshSignal] = useState<{
    conversationId: string;
    nonce: number;
  } | null>(null);

  useEffect(() => {
    setReplyParentHydration({});
  }, [activeConversationId]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [participantProfiles, setParticipantProfiles] = useState<Record<string, PublicIdentity>>({});
  const {
    signingKeyCache,
    pqSigningKeyCache,
    resolveParticipants,
    refreshParticipantProfile,
    notePeerSigningKeys,
    refetchPqSigningKey,
    canRefetchPqSigningKey,
  } = useConversationParticipantProfiles(api, setParticipantProfiles);
  const messagesStateRef = useRef(messagesState);
  const conversationsRef = useRef(conversations);
  const sessionKeyCache = useRef(new Map<string, Uint8Array>());
  const metadataKeyCacheRef = useRef(new Map<string, Uint8Array>());
  const conversationPreferencesRef = useRef(conversationPreferences);
  conversationPreferencesRef.current = conversationPreferences;
  const [metadataHydrateDone, setMetadataHydrateDone] = useState(false);
  messagesStateRef.current = messagesState;
  conversationsRef.current = conversations;

  const isAtBottomRef = useRef(true);
  const syncAutoReadConversationRef = useRef<() => void>(() => {});
  const setIsAtBottom = useCallback((value: boolean) => {
    isAtBottomRef.current = value;
    syncAutoReadConversationRef.current();
  }, []);

  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const decryptConversationName = useCallback(
    (conv: PublicConversation): string | undefined => {
      return decryptConversationNameForDisplay(conv, metadataKeyCacheRef.current.get(conv.id));
    },
    []
  );

  const decryptConversationMemberSettings = useCallback(
    (conv: PublicConversation): MemberSettingsMap | undefined => {
      return decryptConversationMemberSettingsForDisplay(
        conv,
        metadataKeyCacheRef.current.get(conv.id)
      );
    },
    []
  );

  const toDecrypted = useCallback(
    (conv: PublicConversation): DecryptedConversation => ({
      ...conv,
      decryptedName: decryptConversationName(conv),
      decryptedMemberSettings: decryptConversationMemberSettings(conv),
      unreadCount: 0,
      hasUnread: false,
    }),
    [decryptConversationName, decryptConversationMemberSettings]
  );

  const collectRecipientPublicKeys = useCallback(
    async (
      participantIds: string[],
      useForwardSecrecy = false,
      signal?: AbortSignal
    ): Promise<RecipientKeys[]> => {
      const throwIfAborted = () => {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      };
      const recipients: RecipientKeys[] = [];

      for (const pid of participantIds) {
        try {
          throwIfAborted();
          const keysResp = await api.identity.getPublicKeys(
            pid,
            signal ? { signal } : undefined
          );
          if (!keysResp.data) continue;

          let preKeys: ClaimedDevicePreKeys[] = [];
          if (useForwardSecrecy) {
            try {
              throwIfAborted();
              const claimResp = await api.identity.claimPreKeys(
                pid,
                undefined,
                signal ? { signal } : undefined
              );
              if (claimResp.data?.devices) {
                preKeys = claimResp.data.devices;
              }
            } catch (err) {
              if (err instanceof DOMException && err.name === 'AbortError') throw err;
            }
          }

          recipients.push({
            identityId: pid,
            signingPublicKey: keysResp.data.signingPublicKey,
            pqSigningPublicKey: keysResp.data.pqSigningPublicKey,
            preferredCryptoProfile: keysResp.data.preferredCryptoProfile as 'default' | 'cnsa2',
            devices: keysResp.data.devices,
            preKeys,
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') throw err;
        }
      }
      return recipients;
    },
    [api]
  );

  const fetchWrapableRecipientKeys = useCallback(
    async (
      conversationId: string,
      participantIds: string[],
      signal?: AbortSignal
    ): Promise<RecipientKeys[]> => {
      const fetched = await collectRecipientPublicKeys(participantIds, false, signal);
      const pause = isPeerTrustPauseEnabled(identity?.id ?? '');
      const evaluation = await evaluatePeerTrust({
        conversationId,
        fetched,
        requestedParticipantIds: participantIds,
        includeBlockedInWrapable: !pause,
      });
      return evaluation.recipients as RecipientKeys[];
    },
    [collectRecipientPublicKeys, identity?.id]
  );

  /**
   * Fetch recipient keys (identity public keys + pre-keys) for all participants
   * in a conversation, needed for encrypting outbound messages.
   *
   * When `useForwardSecrecy` is false, pre-key claiming is skipped entirely
   * and all devices use static key wrapping. E2EE is still maintained.
   */
  const fetchRecipientKeys = useCallback(
    async (
      conversationId: string,
      participantIds: string[],
      useForwardSecrecy = false,
      signal?: AbortSignal
    ): Promise<RecipientKeys[]> => {
      const fetched = await collectRecipientPublicKeys(
        participantIds,
        useForwardSecrecy,
        signal
      );
      const pause = isPeerTrustPauseEnabled(identity?.id ?? '');
      const evaluation = await evaluatePeerTrust({
        conversationId,
        fetched,
        requestedParticipantIds: participantIds,
        includeBlockedInWrapable: !pause,
      });
      if (pause) throwIfPeerTrustBlocked(evaluation);
      return evaluation.recipients as RecipientKeys[];
    },
    [collectRecipientPublicKeys, identity?.id]
  );

  const mergeEnsuredConversation = useCallback(
    (conversationId: string, updated?: PublicConversation) => {
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== conversationId) return c;
          const next = updated ? { ...c, ...updated } : c;
          return {
            ...next,
            decryptedName: decryptConversationName(next),
            decryptedMemberSettings: decryptConversationMemberSettings(next),
            unreadCount: c.unreadCount,
            hasUnread: c.hasUnread,
          };
        })
      );
    },
    [decryptConversationName, decryptConversationMemberSettings]
  );

  const runEnsureConversationMetadata = useCallback(
    async (
      conversation: PublicConversation,
      options?: { lastReadMessageId?: string }
    ) => {
      if (!identity) return { key: null as Uint8Array | null, wrappedMetadataKeys: undefined as undefined };
      const keys = await loadDecryptKeysQuiet(
        identity.id,
        getCurrentDeviceId(),
        getWrappingKey()
      );
      const wrapable = await fetchWrapableRecipientKeys(
        conversation.id,
        conversation.participants
      );
      const canPersistName =
        conversation.type !== 'group' || conversation.admins.includes(identity.id);
      const pref = conversationPreferencesRef.current[conversation.id];
      return ensureConversationMetadataKey({
        conversation,
        identityId: identity.id,
        ecdhPrivateKey: keys.ecdhPrivateKey,
        kemPrivateKey: keys.kemPrivateKey,
        routingTag: keys.myRoutingTag,
        wrapableRecipients: wrapable,
        cache: metadataKeyCacheRef.current,
        encryptedReadState: pref?.encryptedReadState,
        lastReadMessageId: options?.lastReadMessageId,
        canPersistName,
        canPersistSettings: canPersistName,
        api: {
          updateName: (id, encryptedName, nameNonce, wraps, options) =>
            api.conversations.updateName(
              id,
              encryptedName,
              nameNonce,
              wraps,
              undefined,
              options
            ),
          updateMemberSettings: (id, encrypted, nonce, wraps) =>
            api.conversations.updateMemberSettings(id, encrypted, nonce, wraps),
          updateMetadataKeys: (id, wraps) => api.conversations.updateMetadataKeys(id, wraps),
          updatePreferences: (id, patch) => api.conversations.updatePreferences(id, patch),
        },
      });
    },
    [
      identity,
      getCurrentDeviceId,
      getWrappingKey,
      fetchWrapableRecipientKeys,
      api,
    ]
  );

  const prepareConversationMetadata = useCallback(
    async (conversationOrId: PublicConversation | string) => {
      const conversation =
        typeof conversationOrId === 'string'
          ? conversationsRef.current.find((c) => c.id === conversationOrId)
          : conversationOrId;
      if (!conversation) return null;
      const result = await runEnsureConversationMetadata(conversation);
      if (!result.key || !result.wrappedMetadataKeys?.length) return null;
      if (result.conversation) mergeEnsuredConversation(conversation.id, result.conversation);
      return {
        metadataKey: result.key,
        wrappedMetadataKeys: result.wrappedMetadataKeys,
      };
    },
    [runEnsureConversationMetadata, mergeEnsuredConversation]
  );

  const syncConversationMetadata = useCallback(
    async (conversationId: string) => {
      const conversation = conversationsRef.current.find((c) => c.id === conversationId);
      if (!conversation) return;
      const result = await runEnsureConversationMetadata(conversation);
      if (result.conversation) mergeEnsuredConversation(conversationId, result.conversation);
    },
    [runEnsureConversationMetadata, mergeEnsuredConversation]
  );

  const readPointerPersistQueueRef = useRef(createReadPointerPersistQueue());

  const persistEncryptedReadState = useCallback(
    async (conversationId: string, messageId: string, options?: { force?: boolean }) => {
      await readPointerPersistQueueRef.current.persist(
        conversationId,
        messageId,
        options,
        async () => {
          const conversation = conversationsRef.current.find((c) => c.id === conversationId);
          if (!conversation) return null;
          let metadataKey = metadataKeyCacheRef.current.get(conversationId) ?? null;
          if (!metadataKey) {
            const result = await runEnsureConversationMetadata(conversation, {
              lastReadMessageId: messageId,
            }).catch(() => null);
            if (result?.conversation) mergeEnsuredConversation(conversationId, result.conversation);
            metadataKey = metadataKeyCacheRef.current.get(conversationId) ?? result?.key ?? null;
          }
          const blob = await persistConversationReadPointer({
            conversationId,
            messageId,
            metadataKey,
            updatePreferences: (id, patch) => api.conversations.updatePreferences(id, patch),
          });
          if (blob) mergeEncryptedReadState(conversationId, blob);
          return blob;
        }
      );
    },
    [api, runEnsureConversationMetadata, mergeEnsuredConversation, mergeEncryptedReadState]
  );

  const applyEncryptedReadState = useCallback(
    (prev: DecryptedConversation[]): DecryptedConversation[] => {
      const prefs = conversationPreferencesRef.current;
      const currentActiveId = activeConversationIdRef.current;
      return prev.map((c) => ({
        ...c,
        hasUnread: conversationHasUnreadFromReadState({
          conversationId: c.id,
          lastMessageId: c.lastMessageId,
          encryptedReadState: prefs[c.id]?.encryptedReadState,
          metadataKey: metadataKeyCacheRef.current.get(c.id),
          hasWrappedMetadataKeys: (c.wrappedMetadataKeys?.length ?? 0) > 0,
          isActive: c.id === currentActiveId,
          previousHasUnread: c.hasUnread,
        }),
      }));
    },
    []
  );

  const hydrateConversationMetadata = useCallback(
    async (listed: PublicConversation[]) => {
      try {
        if (!identity) return;
        const keys = await loadDecryptKeysQuiet(
          identity.id,
          getCurrentDeviceId(),
          getWrappingKey()
        );
        if (!keys.ecdhPrivateKey || !keys.kemPrivateKey) return;
        let changed = false;
        for (const conv of listed) {
          if (!conv.wrappedMetadataKeys?.length) continue;
          if (metadataKeyCacheRef.current.has(conv.id)) continue;
          const key = unwrapConversationMetadataKey(
            conv.wrappedMetadataKeys,
            identity.id,
            keys.ecdhPrivateKey,
            keys.kemPrivateKey,
            keys.myRoutingTag
          );
          if (key) {
            metadataKeyCacheRef.current.set(conv.id, key);
            changed = true;
          }
        }
        if (changed) {
          setMessagesState((prev) => {
            let mutated = false;
            const next = { ...prev };
            for (const [convId, state] of Object.entries(prev)) {
              const key = metadataKeyCacheRef.current.get(convId);
              if (!key) continue;
              const messages = state.messages.map((m) => {
                if (m.messageType !== 'system' || !m.encryptedSystemEvent) return m;
                const systemEvent = decryptSystemEventForDisplay(m, convId, key);
                if (systemEvent === m.systemEvent) return m;
                mutated = true;
                return { ...m, systemEvent };
              });
              next[convId] = { ...state, messages };
            }
            return mutated ? next : prev;
          });
        }
        setConversations((prev) =>
          applyEncryptedReadState(
            prev.map((c) => ({
              ...c,
              decryptedName: decryptConversationName(c),
              decryptedMemberSettings: decryptConversationMemberSettings(c),
            }))
          )
        );
      } finally {
        setMetadataHydrateDone(true);
      }
    },
    [
      identity,
      getCurrentDeviceId,
      getWrappingKey,
      decryptConversationName,
      decryptConversationMemberSettings,
      applyEncryptedReadState,
    ]
  );

  // -------------------------------------------------------------------------
  // Signature refresh (late ML-DSA keys)
  // -------------------------------------------------------------------------

  const {
    signatureHooksRef,
    recheckPendingSignatures,
    signatureRefreshNotice,
    dismissSignatureRefreshNotice,
  } = useSignatureRefresh({
    identityId: identity?.id ?? null,
    activeConversationId,
    activeConversationIdRef,
    messagesStateRef,
    replyParentHydrationRef,
    setMessagesState,
    setReplyParentHydration,
    signingKeyCache,
    pqSigningKeyCache,
    refetchPqSigningKey,
    canRefetchPqSigningKey,
  });

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const {
    fetchConversations,
    fetchConversationById,
    fetchMessages,
    fetchMessagesAround,
    loadMessageEditHistory,
    refreshMessageInConversation,
    ensureReplyParentHydration,
    loadPinnedMessagesPage,
    loadThreadPage,
    loadThreadMessage,
    fetchInvites,
    refresh,
  } = useConversationDataFetching({
    isLoggedIn,
    identity,
    api,
    getCurrentDeviceId,
    getWrappingKey,
    toDecrypted,
    resolveParticipants,
    setLoading,
    setConversations,
    setMessagesState,
    setInvites,
    setReplyParentHydration,
    signingKeyCache,
    pqSigningKeyCache,
    sessionKeyCache,
    messagesStateRef,
    conversationsRef,
    activeConversationIdRef,
    isAtBottomRef,
    replyParentHydrationRef,
    replyHydrationInflightRef,
    metadataKeyCacheRef,
    signatureHooksRef,
    toast,
    t,
    onConversationsLoaded: hydrateConversationMetadata,
  });

  const fetchConversationsRef = useRef(fetchConversations);
  fetchConversationsRef.current = fetchConversations;
  const fetchMessagesRef = useRef(fetchMessages);
  fetchMessagesRef.current = fetchMessages;
  const refreshMessageInConversationRef = useRef(refreshMessageInConversation);
  refreshMessageInConversationRef.current = refreshMessageInConversation;

  // -------------------------------------------------------------------------
  // Active conversation
  // -------------------------------------------------------------------------

  const markConversationInboxRead = useCallback(
    (conversationId: string, uptoMessageId?: string) => {
      markItemsReadForConversation(conversationId, uptoMessageId);
      if (!uptoMessageId) return;
      void api.notifications
        .markConversationThreadRead({ conversationId, uptoMessageId })
        .then(() => refreshUnseen())
        .catch(() => {});
    },
    [api, markItemsReadForConversation, refreshUnseen],
  );

  const setActiveConversation = useCallback(
    (id: string | null) => {
      const previousId = activeConversationIdRef.current;
      const leaving = readPointerToPersistOnDeactivate(
        previousId,
        id,
        previousId
          ? preferredReadPointer({
              listedLastMessageId: conversationsRef.current.find((c) => c.id === previousId)
                ?.lastMessageId,
              newestVisibleMessageId: messagesStateRef.current[previousId]?.messages[0]?.id,
              hasNewerPages: messagesStateRef.current[previousId]?.hasNewerPages,
              existingReadPointer:
                decryptConversationReadStateForDisplay(
                  previousId,
                  conversationPreferencesRef.current[previousId]?.encryptedReadState,
                  metadataKeyCacheRef.current.get(previousId)
                ) ?? undefined,
            })
          : undefined,
      );
      if (leaving) {
        void persistEncryptedReadState(leaving.conversationId, leaving.messageId, { force: true });
      }
      setActiveConversationId(id);
      if (id) {
        const conv = conversations.find((c) => c.id === id);
        const hadUnreadCount = conv ? conv.unreadCount > 0 : false;
        const hadUnreadFlag = conv ? conv.hasUnread : false;
        setConversations((prev) =>
          prev.map((c) => (c.id === id ? { ...c, unreadCount: 0, hasUnread: false } : c))
        );
        if (hadUnreadCount || hadUnreadFlag) {
          setMessagesState((prev) => ({
            ...prev,
            [id]: {
              messages: [],
              olderCursor: null,
              newerPaginationAfterId: null,
              hasNewerPages: false,
              loading: true,
              loadingOverlay: false,
              showManualLoadOlder: false,
              showManualLoadNewer: false,
            },
          }));
        }
        fetchMessages(id, undefined, true);

        markConversationInboxRead(id, conv?.lastMessageId);
        if (conv?.lastMessageId) {
          void persistEncryptedReadState(id, conv.lastMessageId, { force: true });
        }
      }
    },
    [conversations, fetchMessages, persistEncryptedReadState, markConversationInboxRead],
  );

  const loadOlder = useCallback(async () => {
    if (!activeConversationId) return;
    const state = messagesState[activeConversationId];
    if (!state?.olderCursor || state.loading) return;
    await fetchMessages(activeConversationId, state.olderCursor, false, false, 'older');
  }, [activeConversationId, messagesState, fetchMessages]);

  const loadNewer = useCallback(async () => {
    if (!activeConversationId) return;
    const state = messagesState[activeConversationId];
    const head = state?.messages[0];
    if (!state?.hasNewerPages || state.loading) return;
    const anchorId = head?.id;
    if (!anchorId) return;
    await fetchMessages(activeConversationId, anchorId, false, false, 'newer');
  }, [activeConversationId, messagesState, fetchMessages]);

  const jumpToLatestMessages = useCallback(
    async (conversationId: string) => {
      if (!isLoggedIn || !identity) return;
      setMessagesState((prev) => ({
        ...prev,
        [conversationId]: {
          messages: [],
          olderCursor: null,
          newerPaginationAfterId: null,
          hasNewerPages: false,
          loading: true,
          loadingOverlay: false,
          showManualLoadOlder: false,
          showManualLoadNewer: false,
        },
      }));
      await fetchMessages(conversationId, undefined, true);
    },
    [isLoggedIn, identity, fetchMessages],
  );

  const { createDM, createGroup, sendTextMessage, editTextMessage } = useConversationCreateAndSend({
    isLoggedIn,
    identity,
    api,
    conversations,
    getSigningKey,
    getPqSigningKey,
    fetchRecipientKeys,
    toDecrypted,
    resolveParticipants,
    setConversations,
    setMessagesState,
    setSending,
    accountSubjectId: session?.identifier,
    prepareConversationMetadata,
    syncConversationMetadata,
    persistEncryptedReadState,
  });

  const {
    addMember,
    removeMember,
    leaveGroup,
    promoteToAdmin,
    onPendingInvitesChanged,
    listPendingGroupInvites,
    revokeGroupInvite,
    terminateGroup,
    renameGroup,
    updateConversationMemberSettings,
    updateGifsDisabled,
    updateGifContentFilter,
    updateCustomEmojisDisabled,
    updateMessageSearchCachePolicy,
    updateAllowSkipModeration,
    updateCallSettings,
    pinMessage,
    unpinMessage,
    deleteMessage,
    acceptInvite,
    declineInvite,
    getInvitePreview,
    getFormerMembers,
  } = useConversationGroupInvitesAndDelete({
    api,
    fetchConversations,
    fetchMessagesRef,
    activeConversationIdRef,
    setConversations,
    setActiveConversationId,
    activeConversationId,
    setPendingInvitesRefreshSignal,
    setInvites,
    setMessagesState,
    toDecrypted,
    identity,
    participantProfiles,
    prepareConversationMetadata,
  });

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------

  const fireNotification = useCallback(
    (title: string, body: string, opts?: { isViewingConvo?: boolean; onClick?: () => void; expiresAt?: string; isMention?: boolean }) => {
      fireConversationNotification(
        title,
        body,
        {
          onClick: opts?.onClick,
          isViewingConversation: opts?.isViewingConvo,
          nativeTag: 'conversation-event',
          expiresAt: opts?.expiresAt,
          isMention: opts?.isMention,
        },
        { toast, soundPref, ttlSoundPref, mentionSoundPref, notifications, audio, onWilhelmScream: () => claimAchievement('wilhelm_scream') }
      );
    },
    [toast, soundPref, ttlSoundPref, mentionSoundPref, audio, notifications, claimAchievement]
  );

  // -------------------------------------------------------------------------
  // WebSocket events (via shared ChatSocket)
  // -------------------------------------------------------------------------

  const markConversationRead = useCallback((conversationId: string, readUpToMessageId?: string) => {
    const listed = conversationsRef.current.find((c) => c.id === conversationId);
    const targetMessageId = readUpToMessageId ?? listed?.lastMessageId;
    markConversationInboxRead(conversationId, targetMessageId);

    setConversations((prev) => {
      const conv = prev.find((c) => c.id === conversationId);
      if (!conv) return prev;

      const alreadyMarked =
        conv.unreadCount === 0 && !conv.hasUnread && !readUpToMessageId;
      if (alreadyMarked) return prev;

      if (targetMessageId) {
        void persistEncryptedReadState(conversationId, targetMessageId, { force: true });
      }

      return prev.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: 0, hasUnread: false } : c
      );
    });
  }, [persistEncryptedReadState, markConversationInboxRead]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const fireNotificationRef = useRef(fireNotification);
  fireNotificationRef.current = fireNotification;

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const resolveParticipantsRef = useRef(resolveParticipants);
  resolveParticipantsRef.current = resolveParticipants;

  const refreshParticipantProfileRef = useRef(refreshParticipantProfile);
  refreshParticipantProfileRef.current = refreshParticipantProfile;

  const onPendingInvitesChangedRef = useRef(onPendingInvitesChanged);
  onPendingInvitesChangedRef.current = onPendingInvitesChanged;

  const participantProfilesRef = useRef(participantProfiles);
  participantProfilesRef.current = participantProfiles;

  const tRef = useRef(t);
  tRef.current = t;

  const notificationDisplayRef = useRef(notificationDisplay.conversations);
  notificationDisplayRef.current = notificationDisplay.conversations;

  const markConversationReadRef = useRef(markConversationRead);
  markConversationReadRef.current = markConversationRead;

  const syncAutoReadConversation = useCallback(() => {
    const id = activeConversationIdRef.current;
    const viewing =
      !!id &&
      isAtBottomRef.current &&
      typeof document !== 'undefined' &&
      document.hasFocus();
    setAutoReadConversation(viewing ? id : null);
  }, [setAutoReadConversation]);
  syncAutoReadConversationRef.current = syncAutoReadConversation;

  useEffect(() => {
    syncAutoReadConversation();
    if (typeof document === 'undefined') return;
    const update = () => syncAutoReadConversation();
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [syncAutoReadConversation, activeConversationId]);

  useConversationsSocketEffects({
    isLoggedIn,
    subscribe,
    onStateChange,
    setConversations,
    setMessagesState,
    setActiveConversationId,
    setInvites,
    identityRef,
    activeConversationIdRef,
    isAtBottomRef,
    messagesStateRef,
    participantProfilesRef,
    fetchConversationsRef,
    fetchMessagesRef,
    refreshMessageInConversationRef,
    refreshRef,
    fireNotificationRef,
    navigateRef,
    resolveParticipantsRef,
    refreshParticipantProfileRef,
    onPendingInvitesChangedRef,
    markConversationReadRef,
    tRef,
    notificationDisplayRef,
    conversationsRef,
    decryptGroupName: (encryptedName, nonce, conversationId) => {
      const conv = conversationsRef.current.find((c) => c.id === conversationId);
      return (
        decryptConversationNameForDisplay(
          {
            id: conversationId,
            encryptedName,
            nameNonce: nonce,
            wrappedMetadataKeys: conv?.wrappedMetadataKeys,
          },
          metadataKeyCacheRef.current.get(conversationId)
        ) ?? ''
      );
    },
  });

  useConversationsAuthLifecycleEffects({
    isLoggedIn,
    setConversations,
    setMessagesState,
    setReplyParentHydration,
    setInvites,
    setActiveConversationId,
    refreshRef,
    activeConversationId,
    messagesState,
    fetchMessages,
  });

  // -------------------------------------------------------------------------
  // Compute hasUnread from encrypted read pointers after metadata keys hydrate
  // and preferences load, so leftover HKDF blobs are not flashed as unread.

  useEffect(() => {
    if (prefsLoading || conversations.length === 0 || !metadataHydrateDone) return;
    setConversations((prev) => {
      const next = applyEncryptedReadState(prev);
      return next.some((c, i) => c.hasUnread !== prev[i]?.hasUnread) ? next : prev;
    });
  }, [prefsLoading, conversations.length, conversationPreferences, metadataHydrateDone, applyEncryptedReadState]);

  const activeReadPointer = preferredReadPointer({
    listedLastMessageId: conversations.find((c) => c.id === activeConversationId)?.lastMessageId,
    newestVisibleMessageId: activeConversationId
      ? messagesState[activeConversationId]?.messages[0]?.id
      : undefined,
    hasNewerPages: activeConversationId
      ? messagesState[activeConversationId]?.hasNewerPages
      : undefined,
    existingReadPointer: activeConversationId
      ? decryptConversationReadStateForDisplay(
          activeConversationId,
          conversationPreferences[activeConversationId]?.encryptedReadState,
          metadataKeyCacheRef.current.get(activeConversationId)
        ) ?? undefined
      : undefined,
  });

  useEffect(() => {
    if (!activeConversationId || !metadataHydrateDone || !activeReadPointer) return;
    void persistEncryptedReadState(activeConversationId, activeReadPointer);
  }, [activeConversationId, activeReadPointer, metadataHydrateDone, persistEncryptedReadState]);

  useEffect(() => {
    if (!isLoggedIn) {
      setMetadataHydrateDone(false);
      readPointerPersistQueueRef.current.clear();
    }
  }, [isLoggedIn]);

  // -------------------------------------------------------------------------
  // Report evidence: session key retrieval
  // -------------------------------------------------------------------------

  const getSessionKeysForMessages = useCallback(
    async (messageIds: string[]): Promise<Record<string, string>> => {
      if (!identity) return {};
      return loadSessionKeysForMessages({
        messageIds,
        identityId: identity.id,
        wrappingKey: getWrappingKey(),
        sessionKeyCache: sessionKeyCache.current,
        getPersistedSessionKey,
      });
    },
    [identity, getWrappingKey],
  );

  const computeAtLiveTail = useCallback((conversationId: string) => {
    const state = messagesStateRef.current[conversationId];
    const conv = conversationsRef.current.find((c) => c.id === conversationId);
    const hadNewerPages = state?.hasNewerPages ?? false;
    const headBefore = state?.messages[0]?.id;
    const lastBefore = conv?.lastMessageId;
    return (
      !hadNewerPages &&
      (lastBefore == null ? headBefore == null : headBefore === lastBefore)
    );
  }, []);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------

  const value: ConversationsContextValue = useMemo(() => {
    const activeState = activeConversationId ? messagesState[activeConversationId] : undefined;
    const activeConversation = activeConversationId
      ? conversations.find((c) => c.id === activeConversationId)
      : undefined;

    return {
      conversations,
      activeConversationId,
      activeMessages: activeState?.messages ?? EMPTY_MESSAGES,
      activeMessagesOlderCursor: activeState?.olderCursor ?? null,
      activeMessagesHasNewerPages: activeState?.hasNewerPages ?? false,
      activeShowManualLoadOlder: activeState?.showManualLoadOlder ?? false,
      activeShowManualLoadNewer: activeState?.showManualLoadNewer ?? false,
      invites,
      participantProfiles,
      memberSettings: activeConversation?.decryptedMemberSettings ?? EMPTY_MEMBER_SETTINGS,
      loading,
      messagesLoading: activeState?.loading ?? false,
      messagesLoadingOverlay: activeState?.loadingOverlay ?? false,
      sending,
      setActiveConversation,
      setIsAtBottom,
      fetchConversationById,
      markConversationRead,
      createDM,
      createGroup,
      sendTextMessage,
      editTextMessage,
      loadMessageEditHistory,
      computeAtLiveTail,
      loadOlder,
      loadNewer,
      jumpToLatestMessages,
      fetchMessagesAround,
      replyParentHydrationMap: activeConversationId
        ? replyParentHydration[activeConversationId] ?? {}
        : {},
      ensureReplyParentHydration,
      loadPinnedMessagesPage,
      loadThreadPage,
      loadThreadMessage,
      deleteMessage,
      addMember,
      removeMember,
      leaveGroup,
      renameGroup,
      updateMemberSettings: updateConversationMemberSettings,
      updateGifsDisabled,
      updateGifContentFilter,
      updateCustomEmojisDisabled,
      updateMessageSearchCachePolicy,
      updateAllowSkipModeration,
      updateCallSettings,
      pinMessage,
      unpinMessage,
      promoteToAdmin,
      terminateGroup,
      acceptInvite,
      declineInvite,
      getInvitePreview,
      getFormerMembers,
      pendingInvitesRefreshSignal,
      listPendingGroupInvites,
      revokeGroupInvite,
      prefetchParticipantProfiles: resolveParticipants,
      fetchRecipientKeys,
      syncConversationMetadata,
      getSessionKeysForMessages,
      notePeerSigningKeys,
      recheckPendingSignatures,
      signatureRefreshNotice:
        signatureRefreshNotice?.conversationId === activeConversationId ? signatureRefreshNotice : null,
      dismissSignatureRefreshNotice,
      refresh,
    };
  }, [
    conversations, activeConversationId, messagesState, replyParentHydration, invites,
    pendingInvitesRefreshSignal,
    participantProfiles, loading, sending,
    setActiveConversation, setIsAtBottom, fetchConversationById, markConversationRead,
    createDM, createGroup, sendTextMessage, editTextMessage, loadMessageEditHistory, computeAtLiveTail, loadOlder, loadNewer, jumpToLatestMessages,
    fetchMessagesAround, ensureReplyParentHydration, loadPinnedMessagesPage, loadThreadPage, loadThreadMessage,
    deleteMessage, addMember, removeMember, leaveGroup, renameGroup,
    updateConversationMemberSettings, updateGifsDisabled, updateGifContentFilter, updateCustomEmojisDisabled, updateMessageSearchCachePolicy, updateAllowSkipModeration, updateCallSettings, pinMessage, unpinMessage, promoteToAdmin, terminateGroup,
    acceptInvite, declineInvite, getInvitePreview, getFormerMembers,
    listPendingGroupInvites, revokeGroupInvite, resolveParticipants,
    fetchRecipientKeys, syncConversationMetadata, getSessionKeysForMessages, refresh,
    notePeerSigningKeys, recheckPendingSignatures, signatureRefreshNotice, dismissSignatureRefreshNotice,
  ]);

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
}
